import React, { useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  TextField,
} from "@material-ui/core";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import CloseIcon from "@material-ui/icons/Close";
import WarningIcon from "@material-ui/icons/Warning";
import PlaceIcon from "@material-ui/icons/Place";
import LocalPharmacyIcon from "@material-ui/icons/LocalPharmacy";
import CameraIcon from "@material-ui/icons/Camera";
import OpacityIcon from "@material-ui/icons/Opacity";
import {
  getFranchiseMatches,
  getPlacesWithAgreementCode,
  isValidAgreementCode,
  normalizeAgreementCode,
} from "../../../utils/pharmacyValidation";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      borderRadius: 18,
      border: "1px solid #e6edf3",
      boxShadow: "0 24px 48px -28px rgba(8, 82, 121, .85)",
      overflow: "hidden",
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    padding: theme.spacing(2, 2.5),
    background: "linear-gradient(145deg, #085279 0%, #0a6e9c 60%, #0077B6 100%)",
    color: "#fff",
  },
  headerIcon: {
    display: "grid",
    placeItems: "center",
    width: 34,
    height: 34,
    borderRadius: 10,
    background: "rgba(255, 255, 255, .16)",
    flexShrink: 0,
  },
  headerText: {
    minWidth: 0,
    flex: 1,
  },
  headerTitle: {
    "&&": {
      padding: 0,
      fontSize: "1rem",
      fontWeight: 700,
      letterSpacing: "-0.01em",
      color: "#fff",
    },
  },
  headerSubtitle: {
    display: "block",
    fontSize: "0.75rem",
    fontWeight: 400,
    color: "rgba(234, 246, 255, .78)",
    marginTop: 2,
  },
  closeButton: {
    color: "rgba(255, 255, 255, .8)",
    "&:hover": {
      color: "#fff",
      background: "rgba(255, 255, 255, .12)",
    },
  },
  content: {
    "&&": {
      padding: theme.spacing(2.5),
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
      minWidth: 0,
    },
  },
  label: {
    fontSize: "0.6875rem",
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "#6d8496",
  },
  codeInput: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      background: "#fcfdfe",
      fontFamily:
        "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
      fontWeight: 700,
      letterSpacing: "0.18em",
      textTransform: "uppercase",
    },
  },
  codeHint: {
    marginTop: 6,
    marginLeft: 2,
    fontSize: "0.75rem",
    lineHeight: 1.45,
    color: "#8497a6",
  },
  preview: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 14,
    border: "1px solid #e2eaf1",
    background: "#f8fbfd",
  },
  previewCode: {
    fontFamily:
      "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
    fontSize: "1rem",
    fontWeight: 700,
    letterSpacing: "0.18em",
    color: "#085279",
    wordBreak: "break-all",
  },
  previewEmpty: {
    fontStyle: "italic",
    fontWeight: 500,
    letterSpacing: "normal",
    color: "#9aabb8",
  },
  warning: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderRadius: 14,
    border: "1px solid #f3d3d3",
    background: "#fff6f6",
    fontSize: "0.8125rem",
    lineHeight: 1.5,
    color: "#8c3030",
  },
  warningIcon: {
    color: "#c62828",
    flexShrink: 0,
    marginTop: 1,
  },
  list: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "flex",
    flexDirection: "column",
    gap: 6,
    maxHeight: 210,
    overflowY: "auto",
  },
  listItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1),
    borderRadius: 12,
    border: "1px solid #eef3f7",
    background: "#fff",
  },
  listIcon: {
    display: "grid",
    placeItems: "center",
    width: 30,
    height: 30,
    borderRadius: 9,
    background: "#eaf3fa",
    color: "#0077B6",
    flexShrink: 0,
  },
  listText: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  },
  listName: {
    fontSize: "0.8125rem",
    fontWeight: 600,
    color: "#0f3a52",
    wordBreak: "break-word",
  },
  listCity: {
    fontSize: "0.75rem",
    color: "#7b8fa0",
  },
  franchise: {
    padding: theme.spacing(1.5),
    borderRadius: 14,
    border: "1px solid #cfe4f2",
    background: "#f2f8fd",
  },
  franchiseTitle: {
    display: "block",
    fontSize: "0.8125rem",
    fontWeight: 700,
    color: "#085279",
    marginBottom: theme.spacing(0.75),
  },
  franchiseNames: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "flex",
    flexDirection: "column",
    gap: 4,
    marginBottom: theme.spacing(0.5),
  },
  franchiseName: {
    fontSize: "0.75rem",
    color: "#42596b",
    wordBreak: "break-word",
  },
  include: {
    margin: 0,
    color: "#0f3a52",
  },
  empty: {
    padding: theme.spacing(1.5),
    borderRadius: 14,
    border: "1px dashed #d8e4ed",
    fontSize: "0.8125rem",
    color: "#7b8fa0",
    textAlign: "center",
  },
  actions: {
    "&&": {
      padding: theme.spacing(2, 2.5),
      borderTop: "1px solid #eef3f7",
      gap: theme.spacing(1),
    },
  },
  dangerButton: {
    "&&": {
      background: "#c62828",
      color: "#fff",
      "&:hover": {
        background: "#ad1f1f",
      },
      "&.Mui-disabled": {
        background: "#e8b7b7",
        color: "#fff",
      },
    },
  },
}));

const TYPE_ICONS = {
  phar: LocalPharmacyIcon,
  img: CameraIcon,
  lab: OpacityIcon,
};

/**
 * Modal to assign (or remove) the agreement code of several places at once.
 *
 * When the selected places look like branches of the same franchise, the
 * other branches are offered in a highlighted block and they are included by
 * default, the admin can uncheck them.
 */
export default function AgreementCodeDialog({
  open,
  mode,
  places = [],
  allPlaces = [],
  initialCode = "",
  t,
  onClose,
  onConfirm,
}) {
  const classes = useStyles();
  const isAssign = mode !== "remove";

  const [code, setCode] = useState(initialCode || "");
  const [includeFranchise, setIncludeFranchise] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // every time the modal opens it starts from scratch
  useEffect(() => {
    if (open) {
      setCode(normalizeAgreementCode(initialCode) || "");
      setIncludeFranchise(true);
      setSubmitting(false);
    }
  }, [open, initialCode]);

  // Removing the code is destructive, so it only affects the places that were
  // selected and that really have a code.
  const selectedPlaces = useMemo(
    () => (isAssign ? places : getPlacesWithAgreementCode(places)),
    [isAssign, places]
  );

  // The suggestion of the franchise is only offered when the code is assigned:
  // removing it must never reach the branches that were not selected.
  const franchiseMatches = useMemo(
    () => (isAssign ? getFranchiseMatches(selectedPlaces, allPlaces) : []),
    [isAssign, selectedPlaces, allPlaces]
  );

  const affectedPlaces = useMemo(
    () =>
      includeFranchise ? [...selectedPlaces, ...franchiseMatches] : selectedPlaces,
    [selectedPlaces, franchiseMatches, includeFranchise]
  );

  const normalized = normalizeAgreementCode(code) || "";
  const isValid = isValidAgreementCode(normalized);

  const handleCodeChange = (event) => {
    setCode(normalizeAgreementCode(event.target.value) || "");
  };

  const handleConfirm = async () => {
    if (isAssign && (!isValid || affectedPlaces.length === 0)) {
      return;
    }

    setSubmitting(true);
    try {
      await onConfirm(affectedPlaces, isAssign ? normalized : null);
    } catch (error) {
      console.log(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      className={classes.dialog}
    >
      <div className={classes.header}>
        <span className={classes.headerIcon}>
          <LocalOfferIcon fontSize="small" />
        </span>
        <div className={classes.headerText}>
          <DialogTitle className={classes.headerTitle}>
            {isAssign
              ? t("Assign agreement code")
              : t("Remove agreement code")}
          </DialogTitle>
          <span className={classes.headerSubtitle}>
            {isAssign
              ? t("The same code is assigned to every selected place.")
              : t("The agreement code will be removed from the selected places.")}
          </span>
        </div>
        <IconButton
          aria-label={t("Close")}
          className={classes.closeButton}
          onClick={onClose}
          disabled={submitting}
        >
          <CloseIcon />
        </IconButton>
      </div>

      <DialogContent className={classes.content}>
        {isAssign && (
          <>
            <TextField
              label={t("Agreement code")}
              variant="outlined"
              fullWidth
              autoFocus
              value={code}
              onChange={handleCodeChange}
              className={classes.codeInput}
              inputProps={{ maxLength: 50 }}
              helperText={
                <span className={classes.codeHint}>
                  {t(
                    "The code is saved in upper case and it can use letters, numbers, hyphens and underscores."
                  )}
                </span>
              }
            />

            <div className={classes.preview}>
              <span className={classes.label}>{t("Preview")}</span>
              {normalized === "" ? (
                <span className={`${classes.previewCode} ${classes.previewEmpty}`}>
                  {t("Agreement code")}
                </span>
              ) : (
                <span className={classes.previewCode}>{normalized}</span>
              )}
            </div>
          </>
        )}

        {!isAssign && (
          <div className={classes.warning}>
            <WarningIcon fontSize="small" className={classes.warningIcon} />
            <span>
              {t(
                "The agreement code will be removed from the selected places. The patients will not see a discount code in them."
              )}
            </span>
          </div>
        )}

        <div>
          <span className={classes.label}>{t("Places affected")}</span>
          {affectedPlaces.length === 0 ? (
            <div className={classes.empty}>
              {isAssign
                ? t("No places selected")
                : t("None of the selected places has an agreement code")}
            </div>
          ) : (
            <ul className={classes.list}>
              {affectedPlaces.map((place) => {
                const Icon = TYPE_ICONS[place.type] || PlaceIcon;

                return (
                  <li key={place.id} className={classes.listItem}>
                    <span className={classes.listIcon}>
                      <Icon fontSize="small" />
                    </span>
                    <span className={classes.listText}>
                      <span className={classes.listName}>{place.name}</span>
                      <span className={classes.listCity}>
                        {place.city || t("City")}
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {franchiseMatches.length > 0 && (
          <div className={classes.franchise}>
            <span className={classes.franchiseTitle}>
              {t("N branches of the same franchise", {
                count: franchiseMatches.length,
              })}
            </span>
            <ul className={classes.franchiseNames}>
              {franchiseMatches.map((place) => (
                <li key={place.id} className={classes.franchiseName}>
                  {place.name}
                </li>
              ))}
            </ul>
            <FormControlLabel
              className={classes.include}
              control={
                <Checkbox
                  checked={includeFranchise}
                  onChange={(event) =>
                    setIncludeFranchise(event.target.checked)
                  }
                  color="primary"
                />
              }
              label={t("Include them")}
            />
          </div>
        )}
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button
          variant="outlined"
          color="default"
          onClick={onClose}
          disabled={submitting}
        >
          {t("Cancel")}
        </Button>
        {isAssign ? (
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirm}
            disabled={!isValid || affectedPlaces.length === 0 || submitting}
          >
            {t("Assign agreement code")}
          </Button>
        ) : (
          <Button
            variant="contained"
            className={classes.dangerButton}
            onClick={handleConfirm}
            disabled={affectedPlaces.length === 0 || submitting}
          >
            {t("Remove the agreement code from N places", {
              count: affectedPlaces.length,
            })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
